// Copyright 2024, Durable

/**
 * This file provides a reusable way to connect to EventSource (SSE)
 * endpoints using a simplified API with auth headers.
 */

import { useCallback, useEffect, useState } from "react";
import {
  fetchEventSource,
  EventSourceMessage
} from "@microsoft/fetch-event-source";
import { DurableProfile } from "../components/shared/Auth";

import { logging } from "../logging";

/**
 * Error when it fails to connect
 */
class FailedToConnectError extends Error {}

type EventSourceProps = {
  url: string;
  userProfile: DurableProfile;
  onMessage: (message: any) => void;
  onConnected: (connected: boolean) => void;
  openWhenHidden: boolean;
};

/**
 * Connect to the stream of server side events to recieve updates about changes
 * on the job and current state of the synthesizer.
 *
 * Usually, we will tend to keep connections open even when the tab is hidden,
 * as we have relatively few connections open at a time (at most 3 per tab) that
 * don't tend to send too much information.
 */
async function connectEventSource(props: EventSourceProps) {
  const { url, userProfile, onMessage, onConnected, openWhenHidden } = props;

  if (!userProfile) {
    return () => null;
  }

  /**
   * Connect for the first time.
   */
  const onSseConnection = async (response: Response) => {
    const statusCode = response.status;
    if (!response.ok) {
      logging.info(`SSE failed to connect with (${statusCode})`);
      throw new FailedToConnectError();
    }

    logging.info(`SSE connected with (${statusCode}) on ${url}`);
    onConnected(true);
  };

  /**
   * Pull down data from the server. Parse it to a job message or
   * synthesizer state update.
   */
  const onSseMessage = (message: EventSourceMessage) => {
    // If we receive a message with no data, ignore it.
    if (message.data.length == 0) {
      return;
    }
    const data = JSON.parse(message.data);
    onMessage(data);
  };

  /**
   * Handle errors.
   */
  const onSseError = (err) => {
    logging.error(`SSE error: ${JSON.stringify(err)} on ${url}`);
    onConnected(false);
    throw err;
  };

  /**
   * Close the socket and clean up.
   */
  const onSseClosed = () => {
    logging.info(`SSE closed on ${url}.`);
    onConnected(false);
  };

  // Initialize an abort controller (same API as fetch) to return to the
  // calling function.
  const signal = new AbortController();

  // Initialize the event source with user token params.
  await fetchEventSource(url, {
    headers: {
      Authorization: `Bearer ${userProfile.token}`
    },
    onopen: onSseConnection,
    onmessage: onSseMessage,
    onerror: onSseError,
    onclose: onSseClosed,
    signal: signal.signal,
    openWhenHidden: openWhenHidden
  });

  const close = () => {
    logging.info(`SSE being closed by callback on ${url}.`);
    signal.abort();
  };

  return close;
}

/**
 * Hook to connect an event source.
 */
export function useEventSource(props: EventSourceProps) {
  const { onConnected } = props;

  const [socketConnected, setSocketConnected] = useState(false);
  const [allowSocketConnectCooldown, setAllowSocketConnectCooldown] =
    useState(true);

  const onConnectedWithCooldown = useCallback(
    (connected) => {
      onConnected(connected);
      setSocketConnected(connected);
      if (!connected) {
        setAllowSocketConnectCooldown(false);
      }
    },
    [onConnected]
  );

  // If we got disconnected, then set a timeout before allowing reconnecting.
  useEffect(() => {
    if (!socketConnected && !allowSocketConnectCooldown) {
      setTimeout(() => setAllowSocketConnectCooldown(true), 5_000);
    }
  }, [allowSocketConnectCooldown, socketConnected]);

  // Continually reconnect and disconnect from the event source.
  useEffect(
    () => {
      if (props.url && !socketConnected && allowSocketConnectCooldown) {
        connectEventSource({
          onConnected: onConnectedWithCooldown,
          ...props
        });
      }
    },
    // Don't include `socketConnected`, since if this closes, we don't
    // want it to infinitely resubscribe.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allowSocketConnectCooldown, onConnectedWithCooldown, props]
  );
}
